import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "store/store";
import { axiosInstance } from "config/axiosConfig";
import { API_ENDPOINTS } from "config/apiConfig";

interface InformationFormData {
  company_name: string;
  postal_code: string;
  prefecture: string;
  address: string;
  building_room: string;
  industry: string;
  contact_last_name: string;
  contact_first_name: string;
  phone_number: string;
  password: string;
  password_confirmation: string;
  number_card_url?: string;
  corp_regist_cert_url?: string;
  type?: number;
}

interface InformationState {
  informationData: any;
  companyReviewData: any;
  updateInformationData: any;
  registrationFormData: InformationFormData | null;
  error: any;
}

const initialState: InformationState = {
  informationData: null,
  companyReviewData: null,
  updateInformationData: null,
  registrationFormData: null,
  error: null,
};

const informationSlice = createSlice({
  name: "information",
  initialState,
  reducers: {
    getInformationDataSuccess: (state, action: PayloadAction<any>) => {
      state.informationData = action.payload;
      state.companyReviewData = action.payload;
      state.error = null;
    },
    getInformationDataFail: (state, action: PayloadAction<any>) => {
      state.informationData = null;
      state.companyReviewData = null;
      state.error = action.payload;
    },
    createInformationDataSuccess: (state, action: PayloadAction<any>) => {
      state.informationData = action.payload;
      state.error = null;
    },
    createInformationDataFail: (state, action: PayloadAction<any>) => {
      state.informationData = null;
      state.error = action.payload;
    },
    updateInformationDataSuccess: (state, action: PayloadAction<any>) => {
      state.updateInformationData = action.payload;
      state.error = null;
    },
    updateInformationDataFail: (state, action: PayloadAction<any>) => {
      state.updateInformationData = null;
      state.error = action.payload;
    },
    setRegistrationFormData: (
      state,
      action: PayloadAction<InformationFormData>,
    ) => {
      state.registrationFormData = action.payload;
      state.error = null;
    },
    updateRegistrationFormData: (
      state,
      action: PayloadAction<Partial<InformationFormData>>,
    ) => {
      if (state.registrationFormData) {
        state.registrationFormData = {
          ...state.registrationFormData,
          ...action.payload,
        };
      } else {
        state.registrationFormData = action.payload as InformationFormData;
      }
      state.error = null;
    },
    clearRegistrationFormData: (state) => {
      state.registrationFormData = null;
      state.error = null;
    },
    clearInformationData: (state) => {
      state.informationData = null;
      state.updateInformationData = null;
      state.companyReviewData = null;
      state.registrationFormData = null;
      state.error = null;
    },
  },
});

export const {
  createInformationDataSuccess,
  clearInformationData,
  createInformationDataFail,
  getInformationDataSuccess,
  getInformationDataFail,
  updateInformationDataSuccess,
  updateInformationDataFail,
  setRegistrationFormData,
  updateRegistrationFormData,
  clearRegistrationFormData,
} = informationSlice.actions;

export const fetchRegisterCompanyInfo =
  (data: any, token: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await axiosInstance.post(
        `${API_ENDPOINTS.COMPANY_INFO}?token=${token}`,
        data,
      );
      dispatch(createInformationDataSuccess(response.data));
    } catch (error: any) {
      dispatch(createInformationDataFail(error.response.data.message));
    }
  };

export const fetchGetCompanyInfo =
  (code: string, query?: object): AppThunk =>
  async (dispatch) => {
    try {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.COMPANY_INFO}/${code}`,
        { params: query },
      );
      dispatch(getInformationDataSuccess(response.data));
    } catch (error: any) {
      dispatch(getInformationDataFail(error.response.data.message));
    }
  };

export default informationSlice.reducer;
